/**
 * Data / State explorer (ADR-0010 S10) — a builder-side reference of every
 * binding a page exposes, so the author doesn't have to remember the shape of
 * `$user` / `$role` / `$queries` / `$page` (and `$item` inside a repeater).
 *
 * Each row is the exact `{{ … }}` expression you'd type, click-to-copy, with a
 * live value sampled from the preview where one exists. It mirrors
 * `PageRenderer.buildScope` 1:1 — if you add a binding to the runtime scope,
 * surface it here too.
 */
import { Check, Copy } from "lucide-react";
import { useState } from "react";

import type { PageQuery, PageVariable } from "@/lib/pageTypes";

interface QueryPreview {
  rows: Record<string, unknown>[];
  total: number;
}

export interface DataPanelProps {
  queries: PageQuery[];
  variables: PageVariable[];
  /** Per-query preview (rows + total), keyed by query name. */
  preview: Record<string, QueryPreview>;
  user: { id: string; email: string; full_name?: string | null } | null;
  /** The app role object exposed as `$role` (`{ slug, name }`). */
  role: { slug?: string; name?: string } | null;
  /** Columns offered as `{{ $item.<col> }}` when the selection sits inside a
   *  repeater (empty otherwise → the `$item` group is hidden). */
  itemColumns?: string[];
  /** Page-state keys created implicitly by components (input `state_key`s +
   *  data_list `select_into` vars) — surfaced under `$page` even though they
   *  aren't declared in `ast.variables` (S13). */
  stateKeys?: string[];
  /** When this page is an overlay (ADR-0023), surface the read-only `$input`
   *  namespace. `inputKeys` are the `$input.<key>` references discovered in the
   *  page (keys come from the opener's `open_overlay` context, so they can't be
   *  enumerated up-front — we list what's already referenced). */
  isOverlay?: boolean;
  inputKeys?: string[];
  /** Insert the binding at the focused expression field's cursor. Returns true
   *  if a field accepted it; when absent or false, the row falls back to
   *  copy-to-clipboard. */
  onInsert?: (expr: string) => boolean;
}

/** Compact, non-crashing value preview for the right-hand hint. */
function fmt(value: unknown): string {
  if (value === undefined || value === null) return "—";
  if (Array.isArray(value)) return `[${value.length}]`;
  if (typeof value === "object") return "{…}";
  const s = String(value);
  return s.length > 28 ? `${s.slice(0, 27)}…` : s;
}

function Binding({
  expr,
  value,
  onInsert,
}: {
  expr: string;
  value?: unknown;
  onInsert?: (expr: string) => boolean;
}) {
  const [copied, setCopied] = useState(false);
  const flash = () => {
    setCopied(true);
    setTimeout(() => setCopied(false), 1200);
  };
  const activate = async () => {
    // Prefer inserting at the focused expression field's cursor; fall back to
    // copy when no field is focused (or the insert was declined).
    if (onInsert?.(expr)) {
      flash();
      return;
    }
    try {
      await navigator.clipboard?.writeText(expr);
      flash();
    } catch {
      /* clipboard blocked (no HTTPS / denied) — nothing to do */
    }
  };
  return (
    <button
      type="button"
      onClick={activate}
      title="Insert at cursor (or copy)"
      className="group flex w-full items-center gap-2 rounded px-1.5 py-1 text-left hover:bg-surface"
    >
      <code className="min-w-0 flex-1 truncate font-mono text-[11px] text-foreground">{expr}</code>
      {value !== undefined && (
        <span className="max-w-[40%] shrink-0 truncate text-[11px] text-muted-foreground">{fmt(value)}</span>
      )}
      {copied ? (
        <Check className="h-3.5 w-3.5 shrink-0 text-success" strokeWidth={2} />
      ) : (
        <Copy className="h-3.5 w-3.5 shrink-0 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100" strokeWidth={1.5} />
      )}
    </button>
  );
}

function Group({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="px-1.5 pb-0.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/70">
        {title}
      </div>
      <div className="flex flex-col">{children}</div>
    </div>
  );
}

export function DataPanel({
  queries,
  variables,
  preview,
  user,
  role,
  itemColumns = [],
  stateKeys = [],
  isOverlay = false,
  inputKeys = [],
  onInsert,
}: DataPanelProps) {
  // State keys not already declared as variables — surfaced so the author can
  // discover/copy them (they're written at runtime by inputs / row selection).
  const declared = new Set(variables.map((v) => v.name));
  const extraKeys = stateKeys.filter((k) => k && !declared.has(k));
  // Thread the insert callback into every binding row without repeating the prop.
  const Bind = (p: { expr: string; value?: unknown }) => <Binding {...p} onInsert={onInsert} />;
  return (
    <div className="flex flex-col gap-3">
      <Group title="User · $user">
        <Bind expr="{{ $user.email }}" value={user?.email} />
        <Bind expr="{{ $user.full_name }}" value={user?.full_name} />
        <Bind expr="{{ $user.id }}" value={user?.id} />
      </Group>

      <Group title="Role · $role">
        <Bind expr="{{ $role.name }}" value={role?.name} />
        <Bind expr="{{ $role.slug }}" value={role?.slug} />
      </Group>

      {isOverlay && (
        <Group title="Overlay input · $input">
          {/* Read-only, seeded by the opener's open_overlay `context`. */}
          <Bind expr="{{ $input }}" />
          {inputKeys.map((k) => (
            <Bind key={k} expr={`{{ $input.${k} }}`} />
          ))}
          {inputKeys.length === 0 && (
            <p className="px-1.5 text-[11px] text-muted-foreground">
              Reference <code>{"{{ $input.<key> }}"}</code> — keys come from the
              opener's open_overlay context.
            </p>
          )}
        </Group>
      )}

      {itemColumns.length > 0 && (
        <Group title="Repeater row · $item">
          {itemColumns.map((c) => (
            <Bind key={c} expr={`{{ $item.${c} }}`} />
          ))}
          <Bind expr="{{ $index }}" />
        </Group>
      )}

      <Group title="Queries · $queries">
        {queries.length === 0 && (
          <p className="px-1.5 text-[11px] text-muted-foreground">No queries yet — add one below.</p>
        )}
        {queries.map((q) => {
          const p = preview[q.name];
          const cols = p?.rows?.[0] ? Object.keys(p.rows[0]) : [];
          return (
            <div key={q.id} className="mb-1">
              <div className="px-1.5 pt-1 text-[11px] font-medium text-foreground">
                {q.name}
                <span className="ml-1 text-muted-foreground">· {p?.total ?? 0} rows</span>
              </div>
              <Bind expr={`{{ $queries.${q.name}.rows }}`} value={p?.rows} />
              <Bind expr={`{{ $queries.${q.name}.total }}`} value={p?.total} />
              {cols.map((c) => (
                <Bind key={c} expr={`{{ $queries.${q.name}.rows[0].${c} }}`} value={p?.rows?.[0]?.[c]} />
              ))}
            </div>
          );
        })}
      </Group>

      <Group title="Page variables · $page">
        {variables.length === 0 && extraKeys.length === 0 && (
          <p className="px-1.5 text-[11px] text-muted-foreground">No variables yet.</p>
        )}
        {variables.map((v) => (
          <Bind key={v.id} expr={`{{ $page.${v.name} }}`} value={v.initial} />
        ))}
        {/* From inputs / row selection (live at runtime — no build-time value). */}
        {extraKeys.map((k) => (
          <Bind key={k} expr={`{{ $page.${k} }}`} />
        ))}
      </Group>
    </div>
  );
}
